import * as XLSX from 'xlsx';
import type { CardSettlementImportRowDto } from '@/types/cardReconciliation.types';

/**
 * The card settlement spreadsheet: its template, and reading one back.
 *
 * Built to the same split as the journal import — parsing happens here because the endpoint's
 * contract is a list of rows, not a file. A settlement file is a transport format; whether any of
 * it reconciles against what the tills recorded is the server's question, not this file's.
 *
 * Nothing here judges a row. A row this parser cannot make sense of is reported with its
 * spreadsheet row number, never guessed at — a settlement line invented by a lenient parser is a
 * payment the bank never made.
 */

interface ColumnSpec {
  /** Written into the template, and matched case- and space-insensitively on read. */
  header: string;
  /** Other spellings accepted on read, because acquirers all name these differently. */
  aliases?: string[];
  width: number;
  note: string;
}

const COLUMNS: ColumnSpec[] = [
  { header: 'Settlement Date', aliases: ['date', 'settle date', 'value date'], width: 16,
    note: 'yyyy-mm-dd — required' },
  { header: 'Terminal ID',     aliases: ['terminal', 'tid', 'pos id'],          width: 14,
    note: 'Which terminal took the money' },
  { header: 'Batch Number',    aliases: ['batch', 'batch no', 'batch #'],       width: 14,
    note: "The acquirer's own batch reference" },
  { header: 'Card Type',       aliases: ['card', 'scheme', 'card scheme'],      width: 12,
    note: 'Visa, Mastercard, Amex …' },
  { header: 'Gross Amount',    aliases: ['gross', 'amount', 'total'],           width: 14,
    note: 'What the customer paid — required' },
  { header: 'MDR Amount',      aliases: ['mdr', 'commission', 'fee', 'charges'], width: 14,
    note: "The acquirer's fee. Blank means none" },
  { header: 'Chargeback',      aliases: ['chargeback amount', 'cb'],            width: 14,
    note: 'Deducted for a disputed transaction' },
  { header: 'Adjustment',      aliases: ['adjustment amount', 'adj'],           width: 14,
    note: 'Any other correction, positive or negative' },
  { header: 'Bank Reference',  aliases: ['bank ref', 'credit reference'],       width: 20,
    note: 'What appears on the bank statement' },
  { header: 'Settlement Ref',  aliases: ['settlement reference', 'ref'],        width: 20,
    note: "The acquirer's own reference" },
  { header: 'Merchant ID',     aliases: ['merchant', 'mid'],                    width: 16,
    note: 'Your merchant number with the acquirer' },
];

const normalise = (value: string) => value.trim().toLowerCase().replace(/[\s_#]+/g, ' ');

/** header text → its position in COLUMNS. Built once, used for every sheet read. */
const HEADER_INDEX = new Map<string, number>(
  COLUMNS.flatMap((c, i) =>
    [c.header, ...(c.aliases ?? [])].map(h => [normalise(h), i] as [string, number])),
);

// ── Template ─────────────────────────────────────────────────────────────────

/**
 * The header row, a note row, and two worked examples.
 *
 * One line carries a fee and one carries a chargeback, because the two most common questions are
 * "where does the commission go" and "what do I do with a disputed transaction" — and both are
 * answered faster by a filled-in row than by any amount of instruction.
 */
export function downloadCardSettlementTemplate(): void {
  const aoa: unknown[][] = [
    COLUMNS.map(c => c.header),
    COLUMNS.map(c => c.note),
    ['2026-09-15', 'TERM-01', 'B2291', 'Visa',       125000, 2500, 0,    0, 'BNK-99218', 'STL-4471', 'MID-3390'],
    ['2026-09-15', 'TERM-02', 'B2292', 'Mastercard',  86400, 1728, 4500, 0, 'BNK-99219', 'STL-4472', 'MID-3390'],
  ];

  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws['!cols'] = COLUMNS.map(c => ({ wch: c.width }));

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Settlement');
  XLSX.writeFile(wb, 'card-settlement-template.xlsx');
}

// ── Reading a sheet back ─────────────────────────────────────────────────────

export interface ParsedSettlementSheet {
  rows: CardSettlementImportRowDto[];
  /** Rows this parser could not read. Reported, never silently dropped. */
  problems: { rowNumber: number; message: string }[];
}

/**
 * A cell that should be a date, as yyyy-MM-dd — or null.
 *
 * Excel hands back a serial number when the cell is formatted as a date and text when it is not,
 * and both are normal in a file an acquirer generated. Anything else returns null: guessing at
 * "09/08" without knowing the author's locale would settle a batch into the wrong month.
 */
function readDate(value: unknown): string | null {
  if (value == null || value === '') return null;

  if (typeof value === 'number') {
    const parsed = XLSX.SSF.parse_date_code(value);
    if (!parsed) return null;
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${parsed.y}-${pad(parsed.m)}-${pad(parsed.d)}`;
  }

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}`;
  }

  const text = String(value).trim();
  return /^\d{4}-\d{2}-\d{2}$/.test(text) ? text : null;
}

/** Money. Blanks, dashes, thousands separators and bracketed negatives all mean a number. */
function readAmount(value: unknown): number {
  if (value == null || value === '' || value === '-') return 0;
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;

  let text = String(value).trim();
  // (1,234.56) is how a spreadsheet writes a negative when it is formatted as currency.
  const bracketed = /^\((.*)\)$/.exec(text);
  if (bracketed) text = '-' + bracketed[1];

  const n = Number(text.replace(/[,\s]/g, ''));
  return Number.isFinite(n) ? n : 0;
}

const readText = (value: unknown): string | null => {
  if (value == null) return null;
  const text = String(value).trim();
  return text.length === 0 ? null : text;
};

/**
 * Reads the first worksheet into rows the import endpoint accepts.
 *
 * `rowNumber` is the spreadsheet's own row number — what the operator sees in Excel's gutter — so
 * anything reported back points at the line they are looking at.
 */
export async function parseCardSettlementFile(file: File): Promise<ParsedSettlementSheet> {
  const buffer = await file.arrayBuffer();
  const wb     = XLSX.read(buffer, { type: 'array' });

  const sheetName = wb.SheetNames[0];
  if (!sheetName) throw new Error('That file has no worksheets.');

  const grid = XLSX.utils.sheet_to_json<unknown[]>(wb.Sheets[sheetName], { header: 1, blankrows: false });
  if (grid.length === 0) throw new Error('That sheet is empty.');

  // Find the header row rather than assuming row 1: acquirer files usually open with a title,
  // the merchant name and a date range before the columns start.
  let headerRow = -1;
  let map: number[] = [];

  for (let i = 0; i < Math.min(grid.length, 25); i++) {
    const candidate = (grid[i] ?? []).map(cell => HEADER_INDEX.get(normalise(String(cell ?? ''))) ?? -1);
    // A header row is one naming a date and an amount. Anything less is a title or a subtotal.
    if (candidate.includes(0) && candidate.includes(4)) { headerRow = i; map = candidate; break; }
  }

  if (headerRow === -1)
    throw new Error(
      'Could not find the column headings. The sheet needs at least Settlement Date and Gross '
    + 'Amount columns — download the template to see the full set.');

  const rows: CardSettlementImportRowDto[] = [];
  const problems: ParsedSettlementSheet['problems'] = [];

  const at = (cells: unknown[], column: number) => {
    const idx = map.indexOf(column);
    return idx === -1 ? null : cells[idx] ?? null;
  };

  for (let i = headerRow + 1; i < grid.length; i++) {
    const cells = grid[i] ?? [];
    const rowNumber = i + 1;                       // what Excel's gutter shows

    // Wholly empty rows are the blank lines people leave between sections, not errors.
    if (cells.every(c => c == null || String(c).trim() === '')) continue;

    const settlementDate = readDate(at(cells, 0));
    const gross          = readAmount(at(cells, 4));

    // A note row from the template, or a totals line at the foot of an acquirer file.
    if (!settlementDate && gross === 0) continue;

    if (!settlementDate) {
      problems.push({ rowNumber, message: 'No settlement date, or not written as yyyy-mm-dd.' });
      continue;
    }

    if (gross === 0) {
      problems.push({ rowNumber, message: 'Gross amount is missing or zero.' });
      continue;
    }

    rows.push({
      lineNo:              rows.length + 1,
      settlementDate,
      terminalId:          readText(at(cells, 1)),
      batchNumber:         readText(at(cells, 2)),
      cardTypeName:        readText(at(cells, 3)),
      grossAmount:         gross,
      mdrAmount:           readAmount(at(cells, 5)),
      chargebackAmount:    readAmount(at(cells, 6)),
      adjustmentAmount:    readAmount(at(cells, 7)),
      bankReference:       readText(at(cells, 8)),
      settlementReference: readText(at(cells, 9)),
      merchantId:          readText(at(cells, 10)),
    });
  }

  if (rows.length === 0 && problems.length === 0)
    throw new Error('That sheet has headings but no settlement lines under them.');

  return { rows, problems };
}

/** What the batch adds up to, shown before anything is sent. */
export function summarise(rows: CardSettlementImportRowDto[]) {
  const sum = (pick: (r: CardSettlementImportRowDto) => number) =>
    rows.reduce((total, r) => total + pick(r), 0);

  const gross      = sum(r => r.grossAmount);
  const mdr        = sum(r => r.mdrAmount);
  const chargeback = sum(r => r.chargebackAmount);
  const adjustment = sum(r => r.adjustmentAmount);

  // Same sum the ledger books as the bank leg: adjustments are deducted, like the fee and chargebacks.
  return { gross, mdr, chargeback, adjustment, net: gross - mdr - chargeback - adjustment };
}
